import useAnimatedMount from '@/hooks/use-animated-mount';
import {
  type Placement,
  arrow,
  autoUpdate,
  offset,
  shift,
  useFloating,
} from '@floating-ui/react';
import { clsx } from 'clsx';
import { useRef } from 'react';

const animationOptions = {
  beforeEntry: clsx('anim-pop-in scale-80 opacity-0'),
  entry: 'anim-pop-in',
  beforeExit: 'anim-pop-out',
  exit: clsx('anim-pop-out scale-80 opacity-0'),
} as const;

interface Props {
  placement?: Placement;
  offset?: number;
  isOpen?: boolean;
  setIsOpen?: (newOpenState: boolean) => void;
}

const useCommonFloating = ({
  placement = 'top',
  offset: offsetAmount = 0,
  isOpen,
  setIsOpen,
}: Props) => {
  const arrowRef = useRef(null);
  const { refs, floatingStyles, context, middlewareData } = useFloating({
    whileElementsMounted: autoUpdate,
    placement,
    middleware: [
      shift(),
      arrow({ element: arrowRef, padding: 9 }),
      offset(offsetAmount),
    ],
    open: isOpen,
    onOpenChange: setIsOpen,
  });

  const {
    mounted,
    className,
    ref: transitionRef,
  } = useAnimatedMount<HTMLDivElement>(isOpen, animationOptions);

  const arrowAxis =
    placement.startsWith('top') || placement.startsWith('bottom') ? 'x' : 'y';

  const arrowOffset =
    (middlewareData.arrow ? middlewareData.arrow[arrowAxis] : 0) ?? 0;

  const crossAxisOffset =
    placement.startsWith('top') || placement.startsWith('left')
      ? 'calc(100% + 9px)'
      : '-9px';

  const offsets = [`${arrowOffset + 9}px`, crossAxisOffset];
  const transformOrigin = (
    arrowAxis === 'x' ? offsets : [...offsets].reverse()
  ).join(' ');
  const transformOriginStyles = {
    transformOrigin,
  };

  return {
    refs,
    transitionRef,
    arrowRef,
    floatingStyles,
    context,
    mounted,
    animationClassName: className,
    transformOriginStyles,
  };
};

export default useCommonFloating;
